import { computed, ref } from 'vue'
import { maxIdSuffix } from '../model/graphCodec'
import type { FlowEdge, FlowNode, FlowSnapshot } from '../model/types'

export const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

/** The graph being edited plus id generation for new nodes and edges. */
export function useFlowDocument() {
  const nodes = ref<FlowNode[]>([
    { id: 'node-start', type: 'start', label: 'Start Session', x: 120, y: 120, data: {} },
  ])
  const edges = ref<FlowEdge[]>([])
  const nodeById = computed(() => new Map(nodes.value.map((node) => [node.id, node])))

  let nodeCounter = 100
  let edgeCounter = 1
  const nextNodeId = () => `node-${++nodeCounter}`
  const nextEdgeId = () => `edge-${++edgeCounter}`

  const snapshot = (): FlowSnapshot => ({ nodes: clone(nodes.value), edges: clone(edges.value) })

  const replace = (snap: FlowSnapshot) => {
    nodes.value = clone(snap.nodes)
    edges.value = clone(snap.edges)
  }

  /** Advances the id counters past ids contained in a loaded graph. */
  const reserveIdsFrom = (snap: FlowSnapshot) => {
    nodeCounter = Math.max(
      nodeCounter,
      maxIdSuffix(
        snap.nodes.map((n) => n.id),
        'node',
      ),
    )
    edgeCounter = Math.max(
      edgeCounter,
      maxIdSuffix(
        snap.edges.map((e) => e.id),
        'edge',
      ),
    )
  }

  return { nodes, edges, nodeById, nextNodeId, nextEdgeId, snapshot, replace, reserveIdsFrom }
}

export type FlowDocument = ReturnType<typeof useFlowDocument>
